import {
  QueryKey,
  useMutation,
  useQueries,
  useQuery,
  UseQueryOptions,
} from "@tanstack/react-query";

import { useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { keys } from "../../core/queries/queries-keys";
import {
  FiatTransactionHistory,
  HistoryTransaction,
  HistoryTxType,
} from "../../core/types/history";
import {
  CountryWithCode,
  ExecuteFiatQuoteResultClient,
  ExecuteQuoteClient,
  FiatCurrencyEnhanced,
  OnrampConfigResponse,
  OnrampPaymentMethod,
  OnrampQuoteResponse,
  OnrampQuoteResult,
  OnRampTransactionStatus,
  OnRampTransactionStatusType,
} from "../../core/types/onramps/onramp";
import { WidgetEvents } from "../../services";
import { OnrampService } from "../../services/external/onrampAdapter";
import { countryCurrencyMap } from "../../services/internal/countryCurrencyMap";
import {
  getCountryData,
  getCurrencyData,
  getSuggestedAmountsForCurrency,
} from "../../services/internal/fiatToCryptoService";

import { isWalletAddressValid } from "../../services/internal/walletService";
import { useSquidChains } from "../chains/useSquidChains";
import {
  ReplaceTransactionStatusParams,
  useHistoryStore,
} from "../store/useHistoryStore";
import { useSquidTokens } from "../tokens/useSquidTokens";

export const TX_STATUS_CONSTANTS = {
  RETRY_COUNT: 2,
  RETRY_DELAY: 60_000, // The provider might return error until the user has time to confirm the transaction
  REFETCH_INTERVAL: 30_000, // Check every 30 seconds
} as const;

/**
 * Final transaction statuses that indicate the transaction has reached a terminal state
 */
export const FINAL_TRANSACTION_STATUSES: OnRampTransactionStatusType[] = [
  "completed",
  "failed",
];

type TransactionStatusQueryOptions = UseQueryOptions<
  OnRampTransactionStatus | undefined,
  Error,
  OnRampTransactionStatus | undefined,
  QueryKey
>;

/**
 * Configuration for transaction status tracking
 */
const createTransactionStatusConfig = ({
  transactionId,
  walletAddress,
  providerId,
  replaceTransactionStatus,
}: {
  transactionId: string;
  walletAddress: string;
  providerId: string;
  replaceTransactionStatus: (params: ReplaceTransactionStatusParams) => void;
}): TransactionStatusQueryOptions => {
  return {
    queryKey: keys().fiatToCryptoStatus(transactionId),
    queryFn: () =>
      new OnrampService().getTransactionStatus(
        transactionId,
        walletAddress,
        providerId
      ),
    enabled: !!transactionId && !!walletAddress && !!providerId,
    retry: TX_STATUS_CONSTANTS.RETRY_COUNT,
    retryDelay: TX_STATUS_CONSTANTS.RETRY_DELAY,
    refetchInterval: (data: OnRampTransactionStatus | undefined) => {
      if (data?.status && FINAL_TRANSACTION_STATUSES.includes(data.status)) {
        return false;
      }
      return TX_STATUS_CONSTANTS.REFETCH_INTERVAL;
    },
    onSuccess: (data) => {
      if (data) {
        replaceTransactionStatus({
          txType: HistoryTxType.BUY,
          orderId: transactionId,
          status: data.status,
          transactionHash: data.transactionHash,
        });
      }
    },
    onError: () => {
      replaceTransactionStatus({
        txType: HistoryTxType.BUY,
        orderId: transactionId,
        status: "failed",
        transactionHash: undefined,
      });
    },
  };
};

/**
 * Fetches quotes for fiat to crypto conversion with provider details.
 * Returns available rates, fees, and supported payment methods for the conversion.
 */
export const useGetFiatQuote = ({
  fiatCurrency,
  cryptoCurrencyID,
  amount,
  region,
  paymentMethod,
  enabled = true,
  onSuccess,
}: {
  fiatCurrency: string;
  cryptoCurrencyID: string;
  amount: number;
  region?: string;
  paymentMethod?: string;
  enabled?: boolean;
  onSuccess?: (quote: OnrampQuoteResponse) => void;
}) => {
  const service = useMemo(() => new OnrampService(), []);
  const { data: config } = useGetOnRampConfig();

  return useQuery<OnrampQuoteResponse>({
    queryKey: keys().fiatToCryptoQuote(
      fiatCurrency,
      cryptoCurrencyID,
      amount,
      region,
      paymentMethod
    ),
    queryFn: async () => {
      // Dispatch event before getting quote
      WidgetEvents.getInstance().dispatchOnrampQuoteRequest({
        fiatCurrency,
        cryptoCurrencyID,
        amount,
        region,
        paymentMethod,
      });

      const quoteResponse = await service.getQuote({
        fiatCurrency,
        cryptoCurrencyID,
        amount,
        region,
        paymentMethod,
      });

      // Enhance quotes with provider information from config
      if (quoteResponse.quotes && config?.supportedOnramps) {
        quoteResponse.quotes = quoteResponse.quotes.map((quote) => ({
          ...quote,
          providerIcon: config?.supportedOnramps?.find(
            (provider) => provider.id === quote.onrampProviderId
          )?.icon,
          providerName:
            config?.supportedOnramps?.find(
              (provider) => provider.id === quote.onrampProviderId
            )?.name || quote.providerName,
        }));
      }

      // If quotes array is empty, throw an error to preserve previous state
      if (quoteResponse.quotes.length === 0) {
        throw new Error("NO_QUOTES_AVAILABLE");
      }

      return quoteResponse;
    },
    enabled: enabled && !!fiatCurrency && !!cryptoCurrencyID && amount > 0,
    onSuccess,
  });
};

/**
 * Fetches and caches onramp provider configuration.
 * Includes supported tokens, payment methods, and country restrictions.
 */
export const useGetOnRampConfig = () => {
  const service = useMemo(() => new OnrampService(), []);
  const { tokens } = useSquidTokens();

  return useQuery<OnrampConfigResponse>({
    queryKey: keys().fiatToCryptoConfig(),
    queryFn: () =>
      service.getConfiguration({
        tokens,
      }),
    cacheTime: 1000 * 60 * 60, // 1 hour
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Executes a fiat to crypto quote and creates an order.
 * Validates wallet address and persists transaction for tracking.
 */
export const useExecuteFiatQuote = () => {
  const service = useMemo(() => new OnrampService(), []);
  const persistTransaction = useHistoryStore(
    (state) => state.persistTransaction
  );
  const { findChain } = useSquidChains();

  return useMutation({
    mutationFn: async (
      executeQuoteRequest: Omit<ExecuteQuoteClient, "orderId"> & {
        orderId?: string;
      }
    ): Promise<ExecuteFiatQuoteResultClient> => {
      const chain = findChain(executeQuoteRequest.squidToken?.chainId);
      if (!chain) {
        throw new Error("Chain not found");
      }

      if (!isWalletAddressValid(chain, executeQuoteRequest.walletAddress)) {
        throw new Error("Invalid wallet address for selected network");
      }

      // Revolut expects us to provide a quoteId, but Onramper already provides one
      const orderId = executeQuoteRequest.orderId ?? uuidv4();

      const executeQuoteRequestWithOrderId = {
        ...executeQuoteRequest,
        orderId,
      };

      // Dispatch event before executing quote
      WidgetEvents.getInstance().dispatchOnrampQuoteExecute(
        executeQuoteRequestWithOrderId
      );

      const executeQuoteResult = await service.executeQuote(
        executeQuoteRequestWithOrderId
      );
      return {
        ...executeQuoteResult,
        executeQuoteRequest: executeQuoteRequestWithOrderId,
        toChain: chain,
      };
    },
    onSuccess: (result: ExecuteFiatQuoteResultClient) => {
      const { executeQuoteRequest, toChain, orderId } = result;
      persistTransaction({
        data: {
          orderId,
          fromFiatCurrency: executeQuoteRequest.fiatCurrency,
          fromAmount: executeQuoteRequest.amount.toString(),
          toCryptoCurrencyID: executeQuoteRequest.cryptoCurrencyID,
          fromCountryCode: executeQuoteRequest.region ?? "",
          toTokenAddress: executeQuoteRequest.squidToken?.address ?? "",
          toCryptoAmount: executeQuoteRequest.cryptoAmount.toString(),
          toChainId: toChain?.chainId ?? "",
          toAddress: executeQuoteRequest.walletAddress,
          status: "awaiting_payment",
          timestamp: Date.now(),
          providerId: executeQuoteRequest.providerId,
        },
        txType: HistoryTxType.BUY,
      });
    },
  });
};

/**
 * Tracks the status of a single fiat to crypto transaction.
 */
export const useFiatOnRampTxStatus = (
  transactionId: string,
  walletAddress: string,
  providerId: string
) => {
  const replaceTransactionStatus = useHistoryStore(
    (state) => state.replaceTransactionStatus
  );

  return useQuery<OnRampTransactionStatus | undefined, Error>({
    ...createTransactionStatusConfig({
      transactionId,
      walletAddress,
      providerId,
      replaceTransactionStatus,
    }),
  });
};

/**
 * Tracks all pending fiat transactions.
 */
export const useFiatTransactions = () => {
  const transactions = useHistoryStore((state) =>
    state.transactions.filter(
      (tx): tx is HistoryTransaction & { txType: HistoryTxType.BUY } =>
        tx.txType === HistoryTxType.BUY
    )
  );
  const replaceTransactionStatus = useHistoryStore(
    (state) => state.replaceTransactionStatus
  );

  const pendingTransactions = useMemo(
    () =>
      transactions?.filter(
        (tx) =>
          !FINAL_TRANSACTION_STATUSES.includes(
            tx.data.status as OnRampTransactionStatusType
          )
      ) ?? [],
    [transactions]
  );

  const queries = useQueries({
    queries: pendingTransactions.map((tx) => ({
      ...createTransactionStatusConfig({
        transactionId: tx.data.orderId,
        walletAddress: tx.data.toAddress,
        providerId: tx.data.providerId,
        replaceTransactionStatus,
      }),
    })),
  });

  return {
    transactions: transactions.map((tx) => tx.data as FiatTransactionHistory),
    isLoading: queries.some((q) => q.isLoading),
    isError: queries.some((q) => q.isError),
  };
};

/**
 * Gets currency details including symbol and limits.
 * Example: USD → { symbol: "$", name: "US Dollar", ... }
 */
export const useCurrencyDetails = (
  currencyCode?: string
): FiatCurrencyEnhanced | undefined => {
  const { data: config } = useGetOnRampConfig();

  return useMemo(() => {
    if (!currencyCode) return undefined;

    const currencyData = getCurrencyData(currencyCode);
    const countryData = countryCurrencyMap.find(
      (c) => c.currencyCode === currencyCode
    );

    if (currencyData) {
      return {
        ...currencyData,
        countryCode: countryData?.countryCode?.toLowerCase(),
      };
    }

    return undefined;
  }, [currencyCode, config?.supportedFiats]);
};

/**
 * Gets country details with flag URL and localized name.
 * Example: "US" → { name: "United States", flag: "🇺🇸", ... }
 */
export const useCountryDetails = (
  countryCode?: string
): CountryWithCode | undefined => {
  return useMemo(() => {
    if (!countryCode) return undefined;
    return getCountryData(countryCode);
  }, [countryCode]);
};

/**
 * Filters and returns only valid quotes that have available payment methods.
 * Ensures users only see quotes they can actually use.
 */
export const useAvailableQuotes = (
  quotes: OnrampQuoteResult[] | undefined
): OnrampQuoteResult[] => {
  return useMemo(() => {
    if (!quotes?.length) return [];
    const validQuotes = quotes.filter(
      (quote) => quote && quote.paymentMethods?.length > 0 && quote.rate
    );
    // Remove duplicates by provider name
    const uniqueQuotes = validQuotes.filter(
      (quote, index, self) =>
        index === self.findIndex((t) => t.providerName === quote.providerName)
    );
    return uniqueQuotes;
  }, [quotes]);
};

/**
 * Gets the recommended quote based on best rates or provider preferences.
 * Falls back to first available quote if no specific recommendation.
 */
export const useRecommendedQuote = (
  quotes: OnrampQuoteResult[] | undefined,
  recommendedQuote?: { onrampProviderId: string; reason?: string }
) => {
  const availableQuotes = useAvailableQuotes(quotes);

  return useMemo(() => {
    if (!availableQuotes.length) return undefined;
    if (!recommendedQuote) return availableQuotes[0];

    // Try to find the recommended quote among available quotes
    const recommendedAvailableQuote = availableQuotes.find(
      (quote) => quote.onrampProviderId === recommendedQuote.onrampProviderId
    );

    // If recommended quote is not available, return first available quote
    return recommendedAvailableQuote ?? availableQuotes[0];
  }, [availableQuotes, recommendedQuote]);
};

/**
 * Fetches the available payment methods for a given fiat and crypto currency pair.
 * The first item in the returned array is the most recommended.
 */
export const useGetOnrampPaymentTypes = ({
  fiatCurrency,
  cryptoCurrencyID,
  region,
  enabled = true,
}: {
  fiatCurrency: string;
  cryptoCurrencyID: string;
  region?: string;
  enabled?: boolean;
}) => {
  const service = useMemo(() => new OnrampService(), []);

  return useQuery<OnrampPaymentMethod[]>({
    queryKey: keys().fiatToCryptoPaymentMethods(
      fiatCurrency,
      cryptoCurrencyID,
      region
    ),
    queryFn: () =>
      service.getPaymentTypes(fiatCurrency, cryptoCurrencyID, region),
    enabled: enabled && !!fiatCurrency && !!cryptoCurrencyID,
  });
};

/**
 * Returns 3 suggested fiat amounts for the given currency code, based on rateAgainstUSD
 */
export function useSuggestedFiatAmounts(currencyCode?: string): number[] {
  return useMemo(() => {
    if (!currencyCode) return [50, 200, 1000];
    return getSuggestedAmountsForCurrency(currencyCode);
  }, [currencyCode]);
}
